/**
 * Canonical dealer profile normalizer (Dealer Profile M2).
 */
import {
  DEALER_PROFILE_FALLBACK_COPY,
  DEALER_PROFILE_PUBLIC_STATUS,
  DEALER_PROFILE_VERIFICATION_STATUS,
} from "../../constants/dealerProfileConstants";
import { LISTING_FALLBACK_COPY } from "../../constants/automapListingConstants";
import { shouldShowListingInSearch } from "../automapListing/listingVisibility";
import {
  isValidDealerPhone,
  isValidDealerWhatsapp,
  normalizeDealerPhone,
  normalizeDealerWhatsapp,
} from "./dealerContact";
import {
  getDealerContactDisabledReason,
} from "./dealerContactRules";
import { buildDealerSlug, getDealerProfileHref } from "./dealerSlug";
import {
  normalizeDealerPublicStatus,
  normalizeDealerVerificationStatus,
} from "./dealerVisibility";

const BLOCKED_DEALER_STATUS = new Set(["suspended", "rejected", "inactive", "archived", "deleted"]);

function sanitizeDisplayString(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function firstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function deriveLocationLabel({ city, department, neighborhood, address }) {
  const parts = [neighborhood, city, department].filter(Boolean);
  if (parts.length) return parts.join(", ");
  return address || DEALER_PROFILE_FALLBACK_COPY.location;
}

function formatResponseTimeLabel(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string" && /\d+\s*min/i.test(raw)) return raw;
  const minutes = Number(raw);
  if (Number.isFinite(minutes) && minutes > 0) return `${minutes} min`;
  return sanitizeDisplayString(raw);
}

function buildTrustBadges(verificationStatus) {
  const badges = [];
  if (verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.VERIFIED) {
    badges.push({ key: "verified", label: "Concesionario verificado" });
  } else if (verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.PENDING) {
    badges.push({ key: "pending", label: "Verificación pendiente" });
  }
  return badges;
}

function buildTrustFlags(dealer = {}) {
  const flags = [];
  if (dealer.offersFinancing === "yes" || dealer.offersFinancing === true) {
    flags.push("financing");
  }
  if (dealer.publicProfileEnabled === true) flags.push("public_profile");
  return flags;
}

/**
 * @param {object|null|undefined} rawDealer
 * @param {{ source?: string, listing?: object|null, slug?: string, usePublicRoute?: boolean, includeRawSource?: boolean }} [options]
 * @returns {import('./dealerProfileTypes').DealerProfile}
 */
export function normalizeDealerProfile(rawDealer, options = {}) {
  const raw = rawDealer || {};
  const listing = options.listing || null;
  const source = options.source || "unknown";

  const id = sanitizeDisplayString(firstDefined(raw.id, raw.dealerId, listing?.dealerId));
  const slug = buildDealerSlug(raw) || buildDealerSlug(listing || {}) || null;

  const name =
    sanitizeDisplayString(
      firstDefined(
        raw.name,
        raw.businessName,
        raw.dealerName,
        listing?.dealerName,
        listing?.sellerName
      )
    ) || DEALER_PROFILE_FALLBACK_COPY.name;

  const displayName = sanitizeDisplayString(firstDefined(raw.displayName, name)) || name;

  const verificationStatus = normalizeDealerVerificationStatus({
    verificationStatus: firstDefined(raw.verificationStatus, raw.dealerVerificationStatus, listing?.dealerVerificationStatus),
    dealerVerified: firstDefined(raw.dealerVerified, raw.isVerified, listing?.dealerVerified),
    status: firstDefined(raw.status, raw.dealerStatus, listing?.dealerStatus),
  });

  const publicStatus = normalizeDealerPublicStatus({
    publicStatus: firstDefined(raw.publicStatus, raw.dealerPublicStatus, listing?.dealerPublicStatus),
    status: firstDefined(raw.status, raw.dealerStatus, listing?.dealerStatus),
    publicProfileEnabled: firstDefined(
      raw.publicProfileEnabled,
      raw.dealerPublicProfileEnabled,
      listing?.dealerPublicProfileEnabled
    ),
  });

  const dealerStatus = sanitizeDisplayString(firstDefined(raw.status, raw.dealerStatus, listing?.dealerStatus));

  const whatsappRaw = firstDefined(
    raw.whatsapp,
    raw.mainWhatsapp,
    raw.primaryContact?.whatsapp,
    listing?.dealerWhatsapp
  );
  const phoneRaw = firstDefined(
    raw.phone,
    raw.mainPhone,
    raw.primaryContact?.phone,
    listing?.dealerPhone
  );

  const whatsapp = isValidDealerWhatsapp(normalizeDealerWhatsapp(whatsappRaw))
    ? normalizeDealerWhatsapp(whatsappRaw)
    : null;
  const phone = isValidDealerPhone(normalizeDealerPhone(phoneRaw))
    ? normalizeDealerPhone(phoneRaw)
    : null;

  const city = sanitizeDisplayString(firstDefined(raw.city, raw.dealerCity, listing?.dealerCity, listing?.city));
  const department = sanitizeDisplayString(firstDefined(raw.department, raw.dealerDepartment, listing?.department));
  const neighborhood = sanitizeDisplayString(
    firstDefined(raw.neighborhood, raw.dealerNeighborhood, listing?.dealerNeighborhood, listing?.neighborhood)
  );
  const address = sanitizeDisplayString(firstDefined(raw.address, raw.dealerAddress));

  const profile = {
    id,
    slug,
    name,
    displayName,
    logoUrl: firstDefined(raw.logoUrl, raw.dealerLogoUrl, listing?.dealerLogoUrl) || null,
    coverImageUrl: firstDefined(raw.coverImageUrl, raw.dealerCoverImageUrl) || null,
    description: sanitizeDisplayString(raw.description) || null,
    city,
    department,
    neighborhood,
    address,
    locationLabel: deriveLocationLabel({ city, department, neighborhood, address }),
    latitude: raw.latitude ?? raw.dealerLatitude ?? null,
    longitude: raw.longitude ?? raw.dealerLongitude ?? null,
    whatsapp,
    phone,
    email: sanitizeDisplayString(firstDefined(raw.email, raw.mainEmail, raw.primaryContact?.email)) || null,
    websiteUrl: sanitizeDisplayString(raw.websiteUrl) || null,
    profileKey: sanitizeDisplayString(firstDefined(raw.profileKey, raw.dealerProfileKey)) || null,
    dealerProfileId: sanitizeDisplayString(firstDefined(raw.dealerProfileId, raw.dealer_profile_id)) || null,
    parentDealerProfileId:
      sanitizeDisplayString(firstDefined(raw.parentDealerProfileId, raw.parent_dealer_profile_id)) || null,
    dealerGroupName:
      sanitizeDisplayString(firstDefined(raw.dealerGroupName, raw.dealer_group_name)) || null,
    geocodeStatus: sanitizeDisplayString(raw.geocodeStatus) || null,
    verificationStatus,
    isVerified: verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.VERIFIED,
    publicSearchExcluded: Boolean(raw.publicSearchExcluded),
    publicStatus,
    dealerStatus,
    trustBadges: buildTrustBadges(verificationStatus),
    trustFlags: buildTrustFlags(raw),
    inventoryCount: raw.inventoryCount ?? raw.activeListingCount ?? listing?.dealerInventoryCount ?? null,
    activeInventoryCount: raw.activeInventoryCount ?? raw.activeListingCount ?? null,
    availableInventoryCount: raw.availableInventoryCount ?? null,
    soldInventoryCount: raw.soldInventoryCount ?? null,
    responseTimeLabel:
      formatResponseTimeLabel(raw.responseTimeLabel) ||
      formatResponseTimeLabel(raw.responseTimeMinutes) ||
      formatResponseTimeLabel(listing?.dealerResponseTimeLabel) ||
      null,
    lastActiveAt: raw.lastActiveAt || raw.updatedAt || null,
    lastInventoryUpdatedAt: raw.lastInventoryUpdatedAt || null,
    verificationApprovedAt: raw.verificationApprovedAt || null,
    joinedAt: raw.joinedAt || raw.createdAt || null,
    profileHref: null,
    contactHref: whatsapp ? `https://wa.me/${whatsapp}` : phone ? buildPhoneContactHref(phone) : null,
    canContact: false,
    contactDisabledReason: null,
    source,
    rawSource: options.includeRawSource ? raw : null,
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
    leadCapture: raw.leadCapture || null,
    leadsEnabled: raw.leadsEnabled,
    leadRoutingMode: raw.leadRoutingMode,
    leadContactAvailability: raw.leadContactAvailability,
  };

  profile.profileHref = getDealerProfileHref(profile, {
    slug: options.slug || "automap",
    usePublicRoute: options.usePublicRoute ?? true,
  });

  if (
    profile.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.UNKNOWN &&
    listing &&
    shouldShowListingInSearch(listing) &&
    (!profile.dealerStatus || !BLOCKED_DEALER_STATUS.has(String(profile.dealerStatus).toLowerCase()))
  ) {
    profile.publicStatus = DEALER_PROFILE_PUBLIC_STATUS.PUBLIC;
  }

  profile.contactDisabledReason = getDealerContactDisabledReason(profile, listing);
  profile.canContact = profile.contactDisabledReason == null;

  return {
    ...profile,
    /** @deprecated Use `name` / `displayName` — kept for gradual UI migration. */
    businessName: profile.displayName || profile.name,
    /** @deprecated Use `whatsapp` — kept for gradual UI migration. */
    mainWhatsapp: profile.whatsapp,
  };
}

function buildPhoneContactHref(phone) {
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length < 10) return null;
  return `tel:+${digits.replace(/^\+/, "")}`;
}

/** @deprecated alias */
export function normalizeDealerFromListing(listing, options = {}) {
  return normalizeDealerProfileFromListing(listing, options);
}

export function normalizeDealerProfileFromListing(listing, options = {}) {
  return normalizeDealerProfileFromSources(
    {
      listing,
      dealerOverride: options.dealerOverride || null,
      rawDealer: options.rawDealer || null,
    },
    { ...options, source: "listing_embedded" }
  );
}

export function normalizeDealerProfileFromApi(rawDealer, options = {}) {
  return normalizeDealerProfile(rawDealer, {
    ...options,
    source: "api",
    includeRawSource: options.includeRawSource ?? false,
  });
}

/**
 * Merge listing embedded dealer with optional API/dealer override.
 */
export function normalizeDealerProfileFromSources({ rawDealer, listing, dealerOverride } = {}, options = {}) {
  const fromListing = listing
    ? {
        id: listing.dealerId,
        name: listing.dealerName,
        slug: listing.dealerSlug,
        logoUrl: listing.dealerLogoUrl,
        dealerVerified: listing.dealerVerified,
        verificationStatus: listing.dealerVerificationStatus,
        city: listing.dealerCity ?? listing.city,
        neighborhood: listing.dealerNeighborhood ?? listing.neighborhood,
        whatsapp: listing.dealerWhatsapp,
        phone: listing.dealerPhone,
        status: listing.dealerStatus,
        publicStatus: listing.dealerPublicStatus,
        publicProfileEnabled: listing.dealerPublicProfileEnabled,
        responseTimeLabel: listing.dealerResponseTimeLabel,
      }
    : {};

  const merged = {
    ...fromListing,
    ...(rawDealer || {}),
    ...(dealerOverride || {}),
  };

  return normalizeDealerProfile(merged, {
    ...options,
    listing,
    source: rawDealer ? "api+listing" : listing ? "listing_embedded" : "manual",
  });
}

export { LISTING_FALLBACK_COPY, DEALER_PROFILE_FALLBACK_COPY };
